import * as amplitude from "@amplitude/analytics-browser";
import { datadogLogs } from "@datadog/browser-logs";
import { TeamWith } from "@redotech/account-common/base-team";
import {
  NumberParam,
  OptionalStringParam,
  StringParam,
  useParam,
} from "@redotech/react-router-util/param";
import {
  useLazyContext,
  useRequiredContext,
} from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { useStore } from "@redotech/react-util/store";
import { usePendingUpdates } from "@redotech/react-util/use-pending-updates";
import { PaymentSchedule } from "@redotech/redo-billing-common/balance/balances";
import { TagKind } from "@redotech/redo-common-model-tags-common/tag";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import {
  canPurchaseLabelStatus,
  FulfillmentGroupStatus,
  HiddenStatuses,
} from "@redotech/redo-fulfillment-group-common/fulfillment-group-status";
import { FulfillmentOrderLineItem } from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import { PrintKind } from "@redotech/redo-fulfillment-group-common/fulfillment-print-status";
import { PrintableDocument } from "@redotech/redo-fulfillment-group-common/print-job";
import { canMergeFulfillmentGroupData } from "@redotech/redo-fulfillment-operations-common/can-merge-fulfillment-groups";
import { canSplitGroup } from "@redotech/redo-fulfillment-operations-common/split-fulfillment-group";
import {
  friendlyViewName,
  OutboundSystemView,
} from "@redotech/redo-fulfillment-operations-common/views/default-outbound-views";
import { OutboundLabelSettings } from "@redotech/redo-fulfillment-settings/outbound-label-settings";
import DotsHorizontalIcon from "@redotech/redo-icons/dots-horizontal.svg";
import PackagePlusIcon from "@redotech/redo-icons/package-plus.svg";
import Plus from "@redotech/redo-icons/plus.svg";
import { GlobalAgentContext } from "@redotech/redo-merchant-app-common/global-agent-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { GroupReplacement } from "@redotech/redo-merchant-fulfillment-rpc/schema/get-replacement-groups";
import { FulfillmentGroupUpdate } from "@redotech/redo-merchant-fulfillment-rpc/schema/update-fulfillment-groups";
import {
  DEFAULT_PAGE_SIZE,
  getFilterCount,
  VALID_PAGE_SIZES,
  viewTypes,
  ViewTypesNames,
} from "@redotech/redo-views-common/views";
import {
  hideNotification,
  showNotification,
  toast,
} from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { CheckboxValue } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoListDropdown } from "@redotech/redo-web/arbiter-components/list/redo-list-dropdown";
import type { BreadcrumbSlot } from "@redotech/redo-web/breadcrumb";
import { Flex } from "@redotech/redo-web/flex";
import { NotificationInstance } from "@redotech/redo-web/notification-provider";
import {
  Action0PortalContext,
  ActionPortalContext,
} from "@redotech/redo-web/page";
import { RowClickHandler, TableRef } from "@redotech/redo-web/table";
import {
  DefaultLoadingContentMessage,
  SaveViewBody,
} from "@redotech/redo-web/table2";
import { bijectionOptional } from "@redotech/util/bijection";
import { sleep } from "@redotech/util/schedule";
import { storeTransform } from "@redotech/util/store";
import { localStore } from "@redotech/web-util/storage";
import { ObjectId } from "bson";
import {
  memo,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AddFundsModalProvider } from "../common/add-funds/add-funds-context";
import { CancelOrderModalProvider } from "../common/cancel-order/cancel-order-context";
import { CreateOrderSlideout } from "../common/create-order-slideout";
import { CreateReturnLabelModalProvider } from "../common/create-return-label/create-return-label-context";
import { EditAddressModalProvider } from "../common/edit-address/edit-address-context";
import { ImportOrdersModal } from "../common/import-orders-modal";
import { MarkAsFulfilledModalProvider } from "../common/mark-as-fulfilled/mark-as-fulfilled-context";
import { MergeModal } from "../common/merge-modal";
import { MergePackingSlipConfirmationModal } from "../common/merge-packing-slip-confirmation-modal";
import {
  MergeSuggestionModal,
  MergeSuggestionSummary,
} from "../common/merge-suggestion-modal";
import { MissingItemsModalProvider } from "../common/missing-items/missing-items-modal-context";
import { PackingSlipBinModalProvider } from "../common/packing-slip-bin-modal-provider";
import { PinVerificationProvider } from "../common/pin-verification-context";
import { PlaceOnHoldModalProvider } from "../common/place-on-hold/place-on-hold-context";
import {
  getDocumentPrintStatusMapForFulfillmentGroups,
  PrintBatchContext,
  PrintBatchProvider,
  workstationIdStore,
} from "../common/print-batch-modal";
import { SubmitShippingClaimModalProvider } from "../common/shipping-claim/shipping-claim-context";
import { getFirstOrderNavigationUrl } from "../common/util";
import { showWatchedGroupsNotification } from "../common/watched-group-notifications";
import { usePollUpdates } from "../hooks";
import {
  ActiveOutboundViewContext,
  EditingOutboundViewContext,
  OutboundActiveViewContextProvider,
  SetEditingOutboundViewContext,
} from "../outbound-active-view-context";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";
import {
  OutboundViewsContext,
  ReloadOutboundViewsContext,
} from "../outbound-views";
import { ParcelContext } from "../services/parcel-service";
import { getOptimisticUpdates } from "../utils/get-optimistic-updates";
import { getTeamOMSUsers } from "../utils/get-team-oms-users";
import { logFulfillmentGroupUpdateBatch } from "../utils/log-fulfillment-group-update-batch";
import { getExpandedItems } from "../utils/outbound-labels-utils";
import { updateFulfillmentGroupsWithAlertFailure } from "../utils/update-fulfillment-groups-with-retry";
import {
  statusFilterName,
  statusShouldBeIncluded,
} from "./filters/status-filter";
import {
  fulfillmentGroupSearchNormalize,
  FulfillmentGroupsFetcher,
} from "./fulfillment-group-fetcher";
import { OrderFulfillmentSlideout } from "./fulfillment-group-slideout";
import { FulfillmentOrderTable } from "./fulfillment-group-table";
import { hideSlideoutStore } from "./hide-slideout-checkbox";
import * as ordersCss from "./outbound-labels.module.css";
import { useFulfillmentScrollRestoration } from "./use-fulfillment-scroll-restoration";

enum OmsHeaderAction {
  ViewMergeSuggestions = "view-merge-suggestions",
  ImportOrders = "import-orders",
  CreateNewOrder = "create-new-order",
}

const mergeSuggestionClearedStore = storeTransform(
  localStore("redo.mergeSuggestionCleared"),
  bijectionOptional({
    read: (string) => new Date(string),
    write: (date) => date.toISOString(),
  }),
);

const mergeSuggestionShownTodayStore = storeTransform(
  localStore("redo.mergeSuggestionShownToday"),
  bijectionOptional({
    read: (string) => new Date(string),
    write: (date) => date.toISOString(),
  }),
);

function getSuggestionNotificationInstance(
  mergeSuggestions: MergeSuggestionSummary[],
  setSuggestionModalOpen: (open: boolean) => void,
  setMergeSuggestionCleared: (cleared: Date | undefined) => void,
): NotificationInstance {
  return {
    title: "Merge shipments?",
    description:
      "We found some shipments that have matching information that you may want to merge.",
    onDismiss(): void | Promise<void> {
      setMergeSuggestionCleared(new Date());
    },
    primaryButton: {
      text: `View ${mergeSuggestions.length} merge suggestions`,
      onClick: () => {
        setMergeSuggestionCleared(new Date());
        setSuggestionModalOpen(true);
      },
    },
    featuredIcon: { Icon: PackagePlusIcon, color: "brand" },
  };
}

function getRowSelectedSet(row: FulfillmentGroupData): Set<number> {
  return new Set(
    Array.from({ length: getExpandedItems(row).length }, (_, i) => i),
  );
}

function getCheckedSubItemsForRows({
  loadedFulfillmentGroupsById,
  newSelectionIds,
  checkedSubItems,
}: {
  loadedFulfillmentGroupsById: Record<string, FulfillmentGroupData>;
  newSelectionIds: Set<string>;
  checkedSubItems: Map<string, Set<number>>;
}): Map<string, Set<number>> {
  const newCheckedSubItems = new Map<string, Set<number>>();
  for (const groupId of newSelectionIds) {
    const row = loadedFulfillmentGroupsById[groupId];
    if (row && !checkedSubItems.has(row._id)) {
      newCheckedSubItems.set(groupId, getRowSelectedSet(row));
    } else if (row) {
      newCheckedSubItems.set(
        groupId,
        checkedSubItems.get(row._id) || new Set<number>(),
      );
    }
  }

  return newCheckedSubItems;
}

function getRowCheckboxState({
  loadedFulfillmentGroupsById,
  groupId,
  checkedSubItems,
}: {
  loadedFulfillmentGroupsById: Record<string, FulfillmentGroupData>;
  groupId: string;
  checkedSubItems: Map<string, Set<number>>;
}): CheckboxValue {
  const row = loadedFulfillmentGroupsById[groupId];
  if (!row) {
    return false;
  }
  const subItems = checkedSubItems.get(groupId);
  if (
    !subItems ||
    (subItems.size === 0 && getExpandedItems(row).length !== 0)
  ) {
    return false;
  }
  const allSelected = subItems.size >= getExpandedItems(row).length;
  return allSelected ? true : "indeterminate";
}

function deduplicatedGroups(groups: FulfillmentGroupData[]) {
  const uniqueGroupsMap = new Map();

  for (const group of groups) {
    uniqueGroupsMap.set(group._id, group);
  }

  return Array.from(uniqueGroupsMap.values());
}

function getUnselectedLineItems({
  group,
  checkedSubItems,
}: {
  group: FulfillmentGroupData;
  checkedSubItems: Map<string, Set<number>>;
}): FulfillmentOrderLineItem[] {
  const selectedSubItems = checkedSubItems.get(group._id) || new Set<number>();
  const unselectedExpandedItems = getExpandedItems(group).filter(
    (_, index) => !selectedSubItems.has(index),
  );

  const regroupedMap = new Map<string, FulfillmentOrderLineItem>();

  for (const item of unselectedExpandedItems) {
    const { lineItemId } = item;
    if (regroupedMap.has(lineItemId)) {
      regroupedMap.get(lineItemId)!.quantity += 1;
    } else {
      regroupedMap.set(lineItemId, { ...item, quantity: 1 });
    }
  }

  return Array.from(regroupedMap.values());
}

export const FulfillmentGroupPage = memo(function FulfillmentGroupPage({
  folderBreadcrumb,
  viewBreadcrumb,
}: {
  folderBreadcrumb?: BreadcrumbSlot;
  viewBreadcrumb?: BreadcrumbSlot;
}) {
  const team = useContext(TeamContext);
  const outboundViewsContext = useRequiredContext(OutboundViewsContext);
  if (!team) {
    return null;
  }
  if (!outboundViewsContext.hasInitallyLoaded) {
    return (
      <div className={ordersCss.container}>
        <DefaultLoadingContentMessage borderless />
      </div>
    );
  }

  return (
    <OutboundActiveViewContextProvider>
      <PinVerificationProvider>
        <PrintBatchProvider>
          <MarkAsFulfilledModalProvider>
            <CancelOrderModalProvider>
              <PlaceOnHoldModalProvider>
                <SubmitShippingClaimModalProvider>
                  <AddFundsModalProvider>
                    <EditAddressModalProvider>
                      <CreateReturnLabelModalProvider>
                        <PackingSlipBinModalProvider>
                          <MissingItemsModalProvider>
                            <FulfillmentGroups
                              folderBreadcrumb={folderBreadcrumb}
                              team={team}
                              viewBreadcrumb={viewBreadcrumb}
                            />
                          </MissingItemsModalProvider>
                        </PackingSlipBinModalProvider>
                      </CreateReturnLabelModalProvider>
                    </EditAddressModalProvider>
                  </AddFundsModalProvider>
                </SubmitShippingClaimModalProvider>
              </PlaceOnHoldModalProvider>
            </CancelOrderModalProvider>
          </MarkAsFulfilledModalProvider>
        </PrintBatchProvider>
      </PinVerificationProvider>
    </OutboundActiveViewContextProvider>
  );
});

// TODO: the giant amount of code below has been mostly replicated in the useFulfillmentGroupManager hook for reuse. Test and use the hook
const FulfillmentGroups = memo(function FulfillmentGroups({
  team,
  folderBreadcrumb,
  viewBreadcrumb,
}: {
  team: TeamWith<{ outboundLabels: OutboundLabelSettings }>;
  folderBreadcrumb?: BreadcrumbSlot;
  viewBreadcrumb?: BreadcrumbSlot;
}) {
  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const rpcMerchantClient = useRequiredContext(RedoMerchantRpcClientContext);
  const { ianaTimezone } = useRequiredContext(TeamContext);
  const { ordersOutboundLabelsHref } = useRequiredContext(
    MerchantRoutesContext,
  );

  // Represents the fulfillment groups in the table
  const [rows, setRowsState] = useState<FulfillmentGroupData[]>([]);
  const [hideSlideout, setHideSlideout] = useStore(hideSlideoutStore);

  // Preserve the table scroll position across order-detail navigations so the
  // user returns to the same row they clicked from (OMS-3071 follow-up).
  const { viewName: scrollKeyViewName } = useParams<{ viewName: string }>();
  useFulfillmentScrollRestoration(scrollKeyViewName, rows.length);

  // Always make sure the rows are deduplicated
  const setRows = useHandler(
    (groups: React.SetStateAction<FulfillmentGroupData[]>) => {
      setRowsState((prev) => {
        const newGroups = typeof groups === "function" ? groups(prev) : groups;
        return deduplicatedGroups(newGroups);
      });
    },
  );

  // Represents fulfillment groups that are not in the table, but are still in memory to be in the slideout
  const [additionalGroups, setAdditionalGroupsState] = useState<
    FulfillmentGroupData[]
  >([]);

  // Always make sure the additional groups are deduplicated
  const setAdditionalGroups = useHandler((groups: FulfillmentGroupData[]) => {
    setAdditionalGroupsState(deduplicatedGroups(groups));
  });

  const [singleGroupInParams, setSingleGroupInParams] = useParam(
    new OptionalStringParam("selected"),
  );

  const [selectedGroupIds, setSelectedGroupIds_] = useState<Set<string>>(
    new Set(singleGroupInParams ? [singleGroupInParams] : []),
  );

  const setSelectedGroupIds = useHandler((ids: Set<string>) => {
    setSelectedGroupIds_(ids);
    if (ids.size === 1) {
      setSingleGroupInParams(Array.from(ids)[0]);
    } else {
      setSingleGroupInParams(undefined);
    }
  });

  const [checkedSubItems, setCheckedSubItems] = useState<
    Map<string, Set<number>>
  >(new Map());

  const [mergeModalOpen, setMergeModalOpen] = useState<boolean>(false);
  const [mergeConfirmationModalOpen, setMergeConfirmationModalOpen] =
    useState<boolean>(false);
  const [suggestionModalOpen, setSuggestionModalOpen] =
    useState<boolean>(false);

  const [groupsWaitingForSplit, setGroupsWaitingForSplit] = useState<string[]>(
    [],
  );

  const groupsWaitingForReplacement = useRef<string[]>([]);

  const setGroupsWaitingForReplacement = useHandler((groups: string[]) => {
    groupsWaitingForReplacement.current = groups;
  });

  const [search] = useParam(new StringParam("search", ""));
  useLayoutEffect(() => {
    const normalizedSearch = fulfillmentGroupSearchNormalize(search);
    if (ObjectId.isValid(normalizedSearch)) {
      setSelectedGroupIds(new Set([normalizedSearch]));
    }
  }, [search, setSelectedGroupIds]);

  // The fulfillment groups that are in the table, plus any fulfillment groups no longer in the table that are still in the slideout
  // De duplicated by id
  const { loadedFulfillmentGroups, loadedFulfillmentGroupsById } =
    useMemo(() => {
      const uniqueGroupsMap = new Map();

      for (const group of [...rows, ...additionalGroups]) {
        uniqueGroupsMap.set(group._id, group);
      }

      return {
        loadedFulfillmentGroups: Array.from(uniqueGroupsMap.values()),
        loadedFulfillmentGroupsById: Object.fromEntries(
          uniqueGroupsMap.entries(),
        ),
      };
    }, [rows, additionalGroups]);

  useEffect(() => {
    if (selectedGroupIds.size > 1) {
      setCheckedSubItems(
        getCheckedSubItemsForRows({
          loadedFulfillmentGroupsById,
          newSelectionIds: selectedGroupIds,
          checkedSubItems,
        }),
      );
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroupIds.size]);

  const [lastActionRowId, setLastActionRowId] = useState<string | null>(null);
  const [createOrderSlideoutOpen, setCreateOrderSlideoutOpen] = useState(false);
  const [actionsDropdownAnchor, setActionsDropdownAnchor] =
    useState<HTMLButtonElement | null>(null);
  const [actionsDropdownOpen, setActionsDropdownOpen] = useState(false);

  const [availableTagsLoad, getAvailableTags] = useTriggerLoad(
    async (signal: AbortSignal) => {
      try {
        const { tags } = await rpcMerchantClient.getTeamTags(
          { kind: TagKind.FULFILLMENT_GROUP },
          { signal },
        );
        return tags;
      } catch (e) {
        return [];
      }
    },
  );

  useEffect(() => {
    getAvailableTags();
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeView = useContext(ActiveOutboundViewContext);
  const viewBeingEdited = useContext(EditingOutboundViewContext);
  const setViewBeingEdited = useContext(SetEditingOutboundViewContext);
  const reloadViews = useContext(ReloadOutboundViewsContext);
  const viewsContext = useContext(OutboundViewsContext);
  const batchPrinting = useContext(PrintBatchContext);
  const { setPageContext } = useContext(GlobalAgentContext);
  const [selectedWorkstationId] = useStore(workstationIdStore);

  const folderName = useMemo(() => {
    if (!activeView.folderId) {
      return undefined;
    }
    return viewsContext?.folders.find(
      (folder) => folder._id === activeView.folderId,
    )?.name;
  }, [activeView.folderId, viewsContext?.folders]);
  const viewName = friendlyViewName(activeView);

  // Table2 adopts a different view's complete baseline before paint, so publish
  // the page header in that same commit.
  useLayoutEffect(() => {
    folderBreadcrumb?.next(folderName ?? "");
    viewBreadcrumb?.next(viewName);
  }, [folderBreadcrumb, folderName, viewBreadcrumb, viewName]);

  // Set page context for AI agent routing
  useEffect(() => {
    if (!activeView || activeView.name === OutboundSystemView.CREATE) {
      return;
    }
    const displayName = friendlyViewName(activeView);
    const viewSpecificQuestions: Record<string, string[]> = {
      [OutboundSystemView.AWAITING_SHIPMENT]: [
        "How can I bulk print labels?",
        "How can I print a packing slip?",
      ],
      [OutboundSystemView.SHIPMENTS]: [
        "How can I void a label?",
        "How do I submit a carrier claim?",
      ],
      [OutboundSystemView.INTERNATIONAL]: [
        "How can I print a commercial invoice?",
        "What is an HS code?",
      ],
      [OutboundSystemView.PRIORITY]: ["How do I add filters to this view?"],
      [OutboundSystemView.IN_STORE_PICKUP]: [
        "How do I mark an order as picked up?",
      ],
    };
    const suggestedQuestions = [
      ...(viewSpecificQuestions[activeView.name] ?? []),
      "How can I create a new view?",
    ];
    setPageContext?.({
      pageTitle: displayName,
      pageDescription: `The merchant is looking at their ${displayName} view`,
      preferredAgent: "oms",
      preferredSkill: "oms",
      suggestedQuestions,
      pageData: {
        viewName: activeView.name,
        viewDisplayName: displayName,
        ...(selectedWorkstationId ? { selectedWorkstationId } : {}),
      },
    });
    return () => setPageContext?.(undefined);
  }, [setPageContext, activeView, selectedWorkstationId]);

  const reservedViewNames = useMemo<Set<string>>(() => {
    const filterOutActiveView = Object.values(viewsContext?.views ?? {}).filter(
      (view) => view.name !== activeView?.name,
    );
    const lowerCaseNames = filterOutActiveView.map((view) =>
      view.name.toLowerCase(),
    );
    return new Set(lowerCaseNames);
  }, [viewsContext, activeView?.name]);

  // If we load the page with selected rows, set the last action row id to the last selected row
  if (selectedGroupIds.size > 0 && !lastActionRowId) {
    const lastSelectedRow = Array.from(selectedGroupIds).at(-1) ?? null;
    setLastActionRowId(lastSelectedRow);
  }

  const fetcher = useMemo(
    () =>
      new FulfillmentGroupsFetcher(rpcClient, setRows, undefined, ianaTimezone),
    [rpcClient, ianaTimezone, setRows],
  );

  const workstationPortal = useContext(Action0PortalContext);
  const actionsPortal = useContext(ActionPortalContext);
  const [importOrdersModalOpen, setImportOrdersModalOpen] = useState(false);
  const openImportOrdersModal = useHandler(() => {
    amplitude.logEvent("oms-fulfillment-import-clicked", {
      viewId: activeView?._id,
      viewType: activeView
        ? new Set<string>(Object.values(OutboundSystemView)).has(
            activeView.name,
          )
          ? "system"
          : "custom"
        : "unknown",
    });
    setImportOrdersModalOpen(true);
  });

  const tableRef = useRef<TableRef<FulfillmentGroupData>>(null);

  useEffect(() => {
    fetcher.rows = rows;
    tableRef.current?.externalSetItems?.(rows);
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const groupIds = useMemo(
    () => Object.keys(loadedFulfillmentGroupsById).sort().join("-"),
    [loadedFulfillmentGroupsById],
  );
  const [pageNumber, setPageNumber] = useParam(new NumberParam("page", 0));
  const [rawPageSize, setPageSize] = useParam(
    new NumberParam("page-size", activeView?.pageSize ?? DEFAULT_PAGE_SIZE),
  );

  const pageSize = useMemo(() => {
    return VALID_PAGE_SIZES.includes(rawPageSize as any)
      ? rawPageSize
      : DEFAULT_PAGE_SIZE;
  }, [rawPageSize]);

  const updateGroups = useHandler(async (updates: FulfillmentGroupUpdate[]) => {
    const updatedGroups = await updateFulfillmentGroupsWithAlertFailure(
      rpcClient,
      updates,
      { teamId: team?._id },
    );
    return Object.values(updatedGroups);
  });

  const { updatesPending, hasPendingUpdate, addPendingUpdates } =
    usePendingUpdates({
      updateGroups,
      onUpdatesComplete: (groups) => pushUpdatedGroups(groups),
      identifier: (update) => update.fulfillmentGroupId,
      onBatchReady: logFulfillmentGroupUpdateBatch,
    });

  const pushUpdatedGroups = useHandler((groups: FulfillmentGroupData[]) => {
    const newGroups: FulfillmentGroupData[] = [...loadedFulfillmentGroups];
    for (const group of groups) {
      if (!hasPendingUpdate(group._id)) {
        const index = newGroups.findIndex((row) => row._id === group._id);
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        if (index >= 0 && !shouldIgnoreUpdate(newGroups[index], group)) {
          newGroups[index] = group;
        }
      }
    }

    const newRows = deduplicatedGroups(
      newGroups.filter((group) => rows.some((row) => row._id === group._id)),
    );
    showWatchedGroupsNotification({
      rows,
      newRows,
      printBatch: batchPrinting.printBatch,
    });
    setRows(newRows);

    const updatedAdditionalGroups = newGroups.filter((group) =>
      additionalGroups.some((row) => row._id === group._id),
    );
    setAdditionalGroups(updatedAdditionalGroups);
    void handleGroupsWaitingForSplit(newRows, rows);
  });

  const shouldIgnoreUpdate = (
    originalGroup: FulfillmentGroupData,
    updatedGroup: FulfillmentGroupData,
  ) => {
    // Ignore the update for a voided or merged group if we're waiting for a new group to replace it
    // However, if we were not the user that initiated the void/merge, we should see the update
    // If it was voided/merged, but there is not a pointer to the next group, we should see the update
    if (
      groupsWaitingForReplacement.current.includes(originalGroup._id) &&
      (updatedGroup.status === FulfillmentGroupStatus.Voided ||
        updatedGroup.status === FulfillmentGroupStatus.Merged) &&
      updatedGroup.pointerToNextGroup
    ) {
      return true;
    }
    return false;
  };

  const groupReplacements = useRef<GroupReplacement[]>([]);

  const replaceGroups = useHandler(async (groups: FulfillmentGroupData[]) => {
    const groupsNeedingReplacement = groups
      .filter((group) =>
        groupsWaitingForReplacement.current.includes(group._id),
      )
      .filter((group) => group.pointerToNextGroup)
      .map((g) => g._id);

    if (groupsNeedingReplacement.length) {
      const response = await rpcClient.getReplacementGroups({
        groupIds: groupsNeedingReplacement,
      });
      groupReplacements.current = [
        ...groupReplacements.current,
        ...response.replacements,
      ];

      const groupsStillNeedingReplacement =
        groupsWaitingForReplacement.current.filter(
          (groupId) => !groupsNeedingReplacement.includes(groupId),
        );

      setGroupsWaitingForReplacement(groupsStillNeedingReplacement);

      // Replace all the voided groups at once when they're all done
      if (groupsStillNeedingReplacement.length === 0) {
        replaceRows(groupReplacements.current);
        replaceSelectedRows(groupReplacements.current);
        replaceCheckedRows(groupReplacements.current);
        groupReplacements.current = [];
      }
    }
  });

  const handleGroupsWaitingForSplit = useHandler(
    async (
      groups: FulfillmentGroupData[],
      oldGroups: FulfillmentGroupData[],
    ) => {
      for (const group of groups) {
        const oldGroup = oldGroups.find((g) => g._id === group._id);
        if (
          groupsWaitingForSplit.includes(group._id) &&
          oldGroup &&
          getExpandedItems(group).length < getExpandedItems(oldGroup).length
        ) {
          setCheckedSubItems(
            new Map(checkedSubItems).set(group._id, getRowSelectedSet(group)),
          );
          setGroupsWaitingForSplit(
            groupsWaitingForSplit.filter((groupId) => groupId !== group._id),
          );
        }
      }
    },
  );

  usePollUpdates({
    groups: rows,
    onGroupsUpdated: (newGroups) => {
      pushUpdatedGroups(newGroups);
      void replaceGroups(newGroups);
    },
  });

  const closedGroupsShouldBeIncluded = useMemo(() => {
    const statusFilters = fetcher.currentAdvancedFilters
      .filter((filter) => filter.data.name === statusFilterName)
      .map((filter) => filter.data);
    return statusShouldBeIncluded(FulfillmentGroupStatus.Closed, statusFilters);
  }, [fetcher.currentAdvancedFilters]);

  const groupsWaitingToBeClosed = useRef<string[]>([]);

  const addGroupsWaitingToBeClosed = useHandler((groups: string[]) => {
    if (closedGroupsShouldBeIncluded) {
      return;
    }
    groupsWaitingToBeClosed.current = [
      ...groupsWaitingToBeClosed.current,
      ...groups,
    ];
  });

  const removeGroupsFromWaitingToBeClosed = useHandler((groups: string[]) => {
    groupsWaitingToBeClosed.current = groupsWaitingToBeClosed.current.filter(
      (groupId) => !groups.includes(groupId),
    );
  });

  const removeGroups = useHandler((groups: FulfillmentGroupData[]) => {
    const closedGroupsToRemove = groups.filter(
      (group) =>
        groupsWaitingToBeClosed.current.includes(group._id) &&
        group.status === FulfillmentGroupStatus.Closed,
    );
    const statusGroupsToRemove = groups.filter((group) =>
      HiddenStatuses.includes(group.status),
    );
    const groupsToRemove = [...closedGroupsToRemove, ...statusGroupsToRemove];
    const groupsToRemoveIds = groupsToRemove.map((g) => g._id);

    if (groupsToRemove.length) {
      const newRows = rows.filter(
        (row) => !groupsToRemoveIds.includes(row._id),
      );
      const newAdditionalGroups = [...additionalGroups, ...groupsToRemove];
      setRows(newRows);
      setAdditionalGroups(newAdditionalGroups);
      removeGroupsFromWaitingToBeClosed(groupsToRemoveIds);
      // If all groups are removed, we should refresh the table. Else, we want a smooth removal
      if (newRows.length === 0) {
        refreshTable();
      }
    }
  });

  const addManualGroupsToTable = useHandler(
    (groups: FulfillmentGroupData[]) => {
      setRows([...groups, ...rows]);
      setSelectedGroupIds(new Set(groups.map(({ _id }) => _id)));
    },
  );

  useLoad(
    async (signal) => {
      if (!loadedFulfillmentGroups?.length) {
        return;
      }
      let errorCount = 0;
      while (true) {
        try {
          // Backoff if we're getting back to back errors
          await sleep(Temporal.Duration.from({ seconds: errorCount }), signal);
          let logged = false;
          const start = performance.now();
          const uniqueGroupIds = new Set(
            loadedFulfillmentGroups.map(({ _id }) => _id),
          );
          const iterator = rpcClient.watchFulfillmentGroups(
            {
              fulfillmentGroupIds: Array.from(uniqueGroupIds),
              maxUpdatedAt: loadedFulfillmentGroups.reduce((max, group) => {
                const updatedAt = group.updatedAt ?? new Date();
                return updatedAt > max ? updatedAt : max;
              }, new Date()),
            },
            { signal },
          );

          for await (const { fulfillmentGroups } of iterator) {
            if (!logged) {
              logged = true;
              datadogLogs.logger.info("initial watchFulfillmentGroups", {
                duration: performance.now() - start,
              });
            }

            pushUpdatedGroups(fulfillmentGroups);
            void replaceGroups(fulfillmentGroups);
            void removeGroups(fulfillmentGroups);
            errorCount = 0;
          }
        } catch (e) {
          if (signal.aborted) {
            break;
          }
          errorCount++;
          console.error("Error while watching for updates", e);
        }
      }
    },
    [groupIds],
  );

  const replaceRows = useHandler((replacements: GroupReplacement[]) => {
    let newRows: FulfillmentGroupData[] = [...rows];

    newRows = newRows.map((row) => {
      const match = replacements.find(
        (needsReplacement) => needsReplacement.groupId === row._id,
      );
      return match ? match.replacementGroup : row;
    });

    setRows(newRows);
  });

  const replaceCheckedRows = useHandler((replacements: GroupReplacement[]) => {
    const newCheckedSubItems = new Map<string, Set<number>>(checkedSubItems);

    for (const replacement of replacements) {
      if (newCheckedSubItems.has(replacement.groupId)) {
        newCheckedSubItems.delete(replacement.groupId);
        newCheckedSubItems.set(
          replacement.replacementGroup._id,
          getRowSelectedSet(replacement.replacementGroup),
        );
      }
    }
    setCheckedSubItems(newCheckedSubItems);
  });

  const replaceSelectedRows = useHandler((replacements: GroupReplacement[]) => {
    const newSelectedRows = new Set<string>(selectedGroupIds);
    for (const replacement of replacements) {
      if (selectedGroupIds.has(replacement.groupId)) {
        newSelectedRows.delete(replacement.groupId);
        newSelectedRows.add(replacement.replacementGroup._id);
      }
    }
    setSelectedGroupIds(newSelectedRows);
  });

  const handleUpdates = useHandler(
    async (fulfillmentGroupUpdates: FulfillmentGroupUpdate[]) => {
      const { newData, updates } = getOptimisticUpdates(
        rows,
        fulfillmentGroupUpdates,
      );
      setRows(newData);
      addPendingUpdates(updates);
    },
  );

  const onGroupsFulfillment = useHandler((groupIds: string[]) => {
    // Set groups to pending optimistically
    const newRows = rows.map((row) => {
      if (groupIds.includes(row._id)) {
        return { ...row, status: FulfillmentGroupStatus.Pending };
      }
      return row;
    });
    setRows(newRows);

    const newAdditionalGroups = additionalGroups.map((row) => {
      if (groupIds.includes(row._id)) {
        return { ...row, status: FulfillmentGroupStatus.Pending };
      }
      return row;
    });
    setAdditionalGroups(newAdditionalGroups);

    addGroupsWaitingToBeClosed(groupIds);
  });

  const [parcelsLoad, _] = useLazyContext(ParcelContext);
  const parcelValues = parcelsLoad.value ?? [];

  const viewRef = useRef<viewTypes["fulfillment-groups"] | null>(null);
  const navigate = useNavigate();
  const currentLocation = useLocation();

  const handleViewEditingChange = useHandler((editing: boolean) => {
    if (!editing && activeView.name === OutboundSystemView.CREATE) {
      navigate(-1);
      return;
    }
    setViewBeingEdited(editing);
  });

  const [saveViewLoad, doSaveView] = useTriggerLoad(
    async (signal: AbortSignal) => {
      if (viewRef.current) {
        const view = viewRef.current;
        const eventProperties = {
          columnCount: view.columns.length,
          filterCount: getFilterCount(view),
          mode: view._id ? "edit" : "create",
          pageSize: view.pageSize,
          ...(view._id ? { viewId: view._id } : {}),
          viewType: new Set<string>(Object.values(OutboundSystemView)).has(
            view.name,
          )
            ? "system"
            : "custom",
        };
        amplitude.logEvent(
          "oms-fulfillment-view-save-submitted",
          eventProperties,
        );
        try {
          await rpcClient.saveView({ view }, { signal });
          amplitude.logEvent("oms-fulfillment-view-saved", eventProperties);
          setViewBeingEdited(false);
          await reloadViews?.();
          const targetUrl = `/stores/${team._id}/order-management/outbound-labels/${encodeURIComponent(view.name)}`;

          if (currentLocation.pathname !== targetUrl) {
            navigate(targetUrl);
          }
        } catch (error) {
          amplitude.logEvent(
            "oms-fulfillment-view-save-failed",
            eventProperties,
          );
          throw error;
        }
      }
    },
  );

  const onDeleteView = useHandler(
    async (view: viewTypes["fulfillment-groups"]) => {
      const eventProperties = {
        viewId: view._id,
        viewType: new Set<string>(Object.values(OutboundSystemView)).has(
          view.name,
        )
          ? "system"
          : "custom",
      };
      amplitude.logEvent(
        "oms-fulfillment-view-delete-submitted",
        eventProperties,
      );
      try {
        await rpcClient.deleteFulfillmentGroupView({ name: view.name });
        amplitude.logEvent("oms-fulfillment-view-deleted", eventProperties);
        viewRef.current = null;
        void reloadViews?.();
        navigate(
          `/stores/${team._id}/order-management/outbound-labels/awaiting-shipment`,
        );
      } catch (error) {
        amplitude.logEvent(
          "oms-fulfillment-view-delete-failed",
          eventProperties,
        );
        throw error;
      }
    },
  );

  const saveView = useHandler(
    ({
      name,
      filters,
      filterGroup,
      columns,
      sort,
      _id,
      position,
    }: SaveViewBody) => {
      viewRef.current = {
        table: ViewTypesNames.FULFILLMENT_GROUPS,
        name,
        filters,
        filterGroup,
        columns,
        sort,
        pageSize,
        _id,
        position,
      };
      doSaveView();
    },
  );

  const [balanceLoad, reloadBalance] = useTriggerLoad(
    async (signal: AbortSignal) => {
      try {
        return await rpcClient.getOutboundBalance(null, { signal });
      } catch (e) {
        return { balance: "0", paymentSchedule: PaymentSchedule.PREPAID };
      }
    },
  );

  useEffect(() => {
    reloadBalance();
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const teamUsers = getTeamOMSUsers(team);

  const toggleRowSelection = useHandler(
    (
      id: string,
      e: React.MouseEvent<HTMLDivElement>,
      selected: boolean = getRowCheckboxState({
        loadedFulfillmentGroupsById,
        groupId: id,
        checkedSubItems,
      }) === false, // returns a CheckboxValue
    ) => {
      if (e.shiftKey) {
        shiftSelection(id);
      } else {
        const newSelections = new Set(checkedSubItems.keys());
        const newCheckedSubItems = new Map(checkedSubItems);
        if (selected) {
          newSelections.add(id);
          newCheckedSubItems.set(
            id,
            getRowSelectedSet(loadedFulfillmentGroupsById[id]),
          );
        } else {
          newSelections.delete(id);
          newCheckedSubItems.delete(id);
        }
        setSelectedGroupIds(newSelections);
        setCheckedSubItems(newCheckedSubItems);
        setLastActionRowId(id);
        amplitude.logEvent("oms-fulfillment-selection-changed", {
          direction: selected ? "selected" : "deselected",
          scope: "row",
          selectionCount: newSelections.size,
        });
      }
    },
  );

  const toggleAllSelection = useHandler((selected: boolean) => {
    const newRows = selected
      ? new Set(rows.map((row) => row._id))
      : new Set<string>();

    setCheckedSubItems(
      !selected
        ? new Map()
        : getCheckedSubItemsForRows({
            loadedFulfillmentGroupsById,
            newSelectionIds: newRows,
            checkedSubItems,
          }),
    );
    setSelectedGroupIds(newRows);
    amplitude.logEvent("oms-fulfillment-selection-changed", {
      direction: selected ? "selected" : "deselected",
      scope: "all",
      selectionCount: newRows.size,
    });
  });

  const shiftSelection = useHandler((rowId: string) => {
    const indexOfLastAction = rows.findIndex((r) => r._id === lastActionRowId);
    const rowAlreadySelected = selectedGroupIds.has(rowId);
    const newSelections = new Set(selectedGroupIds);

    if (rowAlreadySelected) {
      newSelections.delete(rowId);
    } else {
      newSelections.add(rowId);
    }

    if (indexOfLastAction !== -1 && selectedGroupIds.size > 0) {
      const indexOfSelected = rows.findIndex((r) => r._id === rowId);
      const min = Math.min(indexOfSelected, indexOfLastAction);
      const max = Math.max(indexOfSelected, indexOfLastAction);

      if (rowAlreadySelected) {
        for (let i = min; i <= max; i++) {
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          newSelections.delete(rows[i]._id);
        }
      } else {
        for (let i = min; i <= max; i++) {
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          newSelections.add(rows[i]._id);
        }
      }
    }

    setSelectedGroupIds(newSelections);
    setCheckedSubItems(
      getCheckedSubItemsForRows({
        loadedFulfillmentGroupsById,
        newSelectionIds: newSelections,
        checkedSubItems,
      }),
    );
    setLastActionRowId(rowId);
    amplitude.logEvent("oms-fulfillment-selection-changed", {
      direction: rowAlreadySelected ? "deselected" : "selected",
      scope: "range",
      selectionCount: newSelections.size,
    });
  });

  const handleRowClick = useHandler<RowClickHandler<FulfillmentGroupData>>(
    (record, e) => {
      if (checkedSubItems.size > 0 || e.shiftKey) {
        toggleRowSelection(record._id, e);
      } else if (hideSlideout) {
        // If the slideout is hidden, a regular row click takes us to order details page
        const navigationUrl = getFirstOrderNavigationUrl(
          record,
          ordersOutboundLabelsHref,
          activeView?.name,
        );
        if (navigationUrl) {
          amplitude.logEvent("oms-fulfillment-row-opened", {
            fulfillmentGroupId: record._id,
            openMode: "details",
            viewId: activeView?._id,
            viewType: activeView
              ? new Set<string>(Object.values(OutboundSystemView)).has(
                  activeView.name,
                )
                ? "system"
                : "custom"
              : "unknown",
          });
          navigate(navigationUrl);
        }
      } else {
        amplitude.logEvent("oms-fulfillment-row-opened", {
          fulfillmentGroupId: record._id,
          openMode: "slideout",
          viewId: activeView?._id,
          viewType: activeView
            ? new Set<string>(Object.values(OutboundSystemView)).has(
                activeView.name,
              )
              ? "system"
              : "custom"
            : "unknown",
        });
        setLastActionRowId(record._id);
        setSelectedGroupIds(new Set([record._id]));
      }
    },
  );

  const handleClearSelection = useHandler(() => {
    const newRows = new Set<string>();
    setSelectedGroupIds(newRows);
    setCheckedSubItems(new Map());
    amplitude.logEvent("oms-fulfillment-selection-changed", {
      direction: "deselected",
      scope: "all",
      selectionCount: 0,
    });
  });

  const toggleSubItemSelection = useHandler(
    (rowId: string, subItemIndex: number) => {
      const newCheckedSubItems = new Map(checkedSubItems);
      const subItemSelections =
        newCheckedSubItems.get(rowId) || new Set<number>();

      if (subItemSelections.has(subItemIndex)) {
        subItemSelections.delete(subItemIndex);
      } else {
        subItemSelections.add(subItemIndex);
      }

      if (subItemSelections.size === 0) {
        newCheckedSubItems.delete(rowId);
        setSelectedGroupIds(
          new Set(Array.from(selectedGroupIds).filter((id) => id !== rowId)),
        );
      } else {
        newCheckedSubItems.set(rowId, subItemSelections);
        setSelectedGroupIds(new Set([...selectedGroupIds, rowId]));
      }

      setCheckedSubItems(newCheckedSubItems);
    },
  );

  const handleCheckClick = useHandler(
    (id: string, e: React.MouseEvent<HTMLDivElement>) => {
      toggleRowSelection(id, e);
    },
  );

  const [refreshSymbol, setRefreshSymbol] = useState<symbol>(Symbol());
  const [searchRefreshSymbol, setSearchRefreshSymbol] =
    useState<symbol>(Symbol());

  const refreshTable = () => {
    void tableRef.current?.refresh();
    setRefreshSymbol(Symbol());
  };

  const waitForRefresh = async () => {
    await tableRef.current?.refresh();
    setRefreshSymbol(Symbol());
  };

  const [mergeSuggestionCleared, setMergeSuggestionCleared] = useStore(
    mergeSuggestionClearedStore,
  );

  const [mergeSuggestionShownToday, setMergeSuggestionShownToday] = useStore(
    mergeSuggestionShownTodayStore,
  );

  const [rejectedSuggestionIds, setRejectedSuggestionIds] = useState<string[]>(
    [],
  );
  const mergeSuggestionsLoad = useLoad(
    async (signal) => {
      const { summaries } = await rpcClient.getMergeSuggestionSummaries(null, {
        signal,
      });
      const today = new Date().toLocaleDateString();
      const hasShownToday =
        mergeSuggestionShownToday?.toLocaleDateString() === today;
      const hasClearedToday =
        mergeSuggestionCleared?.toLocaleDateString() === today;

      if (summaries.length > 0 && !hasClearedToday && !hasShownToday) {
        showNotification(
          getSuggestionNotificationInstance(
            summaries,
            setSuggestionModalOpen,
            setMergeSuggestionCleared,
          ),
        );
        setMergeSuggestionShownToday(new Date());
      }
      setRejectedSuggestionIds([]);
      return summaries;
    },
    [refreshSymbol],
  );

  // Hide merge suggestion notification when navigating away from the page
  useEffect(() => {
    return () => {
      hideNotification();
    };
  }, []);
  const mergeSuggestions = useMemo(() => {
    return (mergeSuggestionsLoad?.value ?? []).filter(
      (summary) => !rejectedSuggestionIds.includes(summary.mergeSuggestionId),
    );
  }, [mergeSuggestionsLoad.value, rejectedSuggestionIds]);

  const selectedFulfillmentGroups = useMemo(
    () =>
      loadedFulfillmentGroups.filter((group) =>
        selectedGroupIds.has(group._id),
      ) as readonly FulfillmentGroupData[],
    [loadedFulfillmentGroups, selectedGroupIds],
  );
  const slideoutOpen = selectedFulfillmentGroups.length > 0 && !hideSlideout;

  useLayoutEffect(() => {
    const headerActionPortals = [workstationPortal, actionsPortal];
    const slideoutHeaderActionsClass = ordersCss.slideoutHeaderActions;
    const slideoutHeaderActionsShiftedClass =
      ordersCss.slideoutHeaderActionsShifted;
    if (slideoutHeaderActionsClass) {
      for (const portal of headerActionPortals) {
        portal?.classList.add(slideoutHeaderActionsClass);
      }
    }
    return () => {
      for (const portal of headerActionPortals) {
        if (slideoutHeaderActionsClass) {
          portal?.classList.remove(slideoutHeaderActionsClass);
        }
        if (slideoutHeaderActionsShiftedClass) {
          portal?.classList.remove(slideoutHeaderActionsShiftedClass);
        }
      }
    };
  }, [actionsPortal, workstationPortal]);

  useLayoutEffect(() => {
    const slideoutHeaderActionsShiftedClass =
      ordersCss.slideoutHeaderActionsShifted;
    if (slideoutHeaderActionsShiftedClass) {
      for (const portal of [workstationPortal, actionsPortal]) {
        portal?.classList.toggle(
          slideoutHeaderActionsShiftedClass,
          slideoutOpen,
        );
      }
    }
  }, [actionsPortal, slideoutOpen, workstationPortal]);

  const setSelectedFulfillmentGroups = useHandler(
    (groups: FulfillmentGroupData[]) => {
      setAdditionalGroups(groups);
      setSelectedGroupIds(new Set(groups.map((group) => group._id)));
    },
  );

  const { canMerge: showMergeButton, reason: mergeDisabledReason } =
    useMemo(() => {
      if (selectedFulfillmentGroups.length < 2) {
        return { canMerge: false, reason: "Must select at least two rows" };
      }
      return canMergeFulfillmentGroupData(selectedFulfillmentGroups);
    }, [selectedFulfillmentGroups]);

  const hasPackingSlipPrinted = useMemo(
    () =>
      selectedFulfillmentGroups.some(
        (group) => group.printStatus?.[PrintKind.PACKING_SLIP] === true,
      ),
    [selectedFulfillmentGroups],
  );

  const handleMergeModalOpen = useHandler(() => {
    if (hasPackingSlipPrinted) {
      setMergeConfirmationModalOpen(true);
    } else {
      setMergeModalOpen(true);
    }
  });

  const handleMergeConfirmation = useHandler(() => {
    setMergeConfirmationModalOpen(false);
    setMergeModalOpen(true);
  });

  const handleMergeConfirmationCancel = useHandler(() => {
    setMergeConfirmationModalOpen(false);
  });

  const somePartiallySelected = useMemo(() => {
    return loadedFulfillmentGroups.some((group) => {
      return (
        getRowCheckboxState({
          loadedFulfillmentGroupsById,
          groupId: group._id,
          checkedSubItems,
        }) === "indeterminate"
      );
    });
  }, [loadedFulfillmentGroups, loadedFulfillmentGroupsById, checkedSubItems]);

  const copySelectedIdsToClipboard = useHandler(async () => {
    if (selectedGroupIds.size === 0) {
      return;
    }

    const idsArray = Array.from(selectedGroupIds);

    let textToCopy: string;
    if (idsArray.length === 1) {
      // If only one ID, copy just the string
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      textToCopy = idsArray[0];
    } else {
      // If multiple IDs, copy as ObjectId array
      const wrappedIds = idsArray.map((id) => `ObjectId("${id}")`);
      textToCopy = `[${wrappedIds.join(", ")}]`;
    }

    try {
      await navigator.clipboard.writeText(textToCopy);
      amplitude.logEvent("oms-fulfillment-ids-copied", {
        fulfillmentGroupCount: selectedGroupIds.size,
      });
    } catch (error) {
      console.log("Error copying shipment ids", error);
    }
  });

  const deselectGroups = useHandler((groupIds: string[]) => {
    const newSelectedGroupIds = new Set(
      Array.from(selectedGroupIds).filter((id) => !groupIds.includes(id)),
    );
    const newCheckedSubItems = new Map(checkedSubItems);

    for (const groupId of groupIds) {
      newCheckedSubItems.delete(groupId);
    }

    setSelectedGroupIds(newSelectedGroupIds);
    setCheckedSubItems(newCheckedSubItems);
  });

  // Add keyboard shortcut for copying selected IDs (Ctrl+Shift+C)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.ctrlKey &&
        event.shiftKey &&
        event.key === "C" &&
        selectedGroupIds.size > 0
      ) {
        event.preventDefault();
        void copySelectedIdsToClipboard();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [selectedGroupIds, copySelectedIdsToClipboard]);

  const [partialSplitLoad, doPartialSplit] = useTriggerLoad(async () => {
    const partiallySelectedGroups = loadedFulfillmentGroups.filter((group) => {
      const checkboxState = getRowCheckboxState({
        loadedFulfillmentGroupsById,
        groupId: group._id,
        checkedSubItems,
      });

      const { canSplit } = canSplitGroup(group);

      return checkboxState === "indeterminate" && canSplit;
    });
    amplitude.logEvent("oms-fulfillment-split-submitted", {
      fulfillmentGroupCount: partiallySelectedGroups.length,
    });

    if (partiallySelectedGroups.length > 1) {
      toast("Starting split shipments...", { variant: "success" });
    }

    const groupIds = partiallySelectedGroups.map((group) => group._id);
    setGroupsWaitingForSplit((prev) => [...prev, ...groupIds]);

    const splitPromises = partiallySelectedGroups.map(async (group) => {
      try {
        const unselectedItems = getUnselectedLineItems({
          group,
          checkedSubItems,
        });

        const result = await rpcClient.splitFulfillmentGroup({
          originalGroupId: group._id,
          itemsToSplit: unselectedItems.map((item) => ({
            fulfillmentOrderLineItemId: item.externalId,
            quantity: item.quantity,
          })),
        });

        if (result.success && result.newGroupIds) {
          const response = await rpcClient.getReplacementGroups({
            groupIds: result.newGroupIds,
          });

          const newGroups = response.replacements.map(
            (replacement) => replacement.replacementGroup,
          );

          setRows((currentRows) => {
            const groupIndex = currentRows.findIndex(
              (row) => row._id === group._id,
            );
            if (groupIndex !== -1) {
              const newRows = [...currentRows];
              newRows.splice(groupIndex + 1, 0, ...newGroups);
              return newRows;
            }
            return currentRows;
          });

          return { success: true, group };
        }

        return { success: false, group, error: "Split failed" };
      } catch (err) {
        return { success: false, group, error: err };
      }
    });

    const results = await Promise.allSettled(splitPromises);

    const failedGroupIds: string[] = [];

    for (const [index, result] of results.entries()) {
      if (
        result.status === "rejected" ||
        (result.status === "fulfilled" && !result.value.success)
      ) {
        const group = partiallySelectedGroups[index];
        failedGroupIds.push(group._id);
        toast("Failed to split shipment", { variant: "error" });
      }
    }

    if (failedGroupIds.length > 0) {
      setGroupsWaitingForSplit((prev) =>
        prev.filter((id) => !failedGroupIds.includes(id)),
      );
    }
    amplitude.logEvent("oms-fulfillment-split-completed", {
      failedCount: failedGroupIds.length,
      fulfillmentGroupCount: partiallySelectedGroups.length,
      succeededCount: partiallySelectedGroups.length - failedGroupIds.length,
    });
  });

  if (!team.settings?.outboundLabels?.newOmsEnabled || !rows) {
    return null;
  }

  return (
    <PinVerificationProvider>
      <MergePackingSlipConfirmationModal
        onCancel={handleMergeConfirmationCancel}
        onConfirm={handleMergeConfirmation}
        open={mergeConfirmationModalOpen}
      />
      <MergeModal
        analyticsSource="manual"
        groups={selectedFulfillmentGroups}
        headerTitle={`Merge shipments (${selectedFulfillmentGroups.length})`}
        onCancel={() => setMergeModalOpen(false)}
        onModalClose={() => {
          setMergeModalOpen(false);
        }}
        open={mergeModalOpen}
        postMerge={async (group: FulfillmentGroupData) => {
          setMergeModalOpen(false);
          await waitForRefresh();
          setSelectedGroupIds(new Set([group._id]));
          setCheckedSubItems(
            new Map(checkedSubItems).set(group._id, getRowSelectedSet(group)),
          );
        }}
      />
      <MergeSuggestionModal
        open={suggestionModalOpen}
        rejectedSuggestionIds={rejectedSuggestionIds}
        setOpen={setSuggestionModalOpen}
        setRejectedSuggestionIds={setRejectedSuggestionIds}
        summaries={mergeSuggestions}
        triggerReload={refreshTable}
      />
      <div className={ordersCss.container}>
        <FulfillmentOrderTable
          activeView={activeView}
          allChecked={rows.length > 0 && checkedSubItems.size >= rows.length}
          availableTagsLoad={availableTagsLoad}
          checkedSubItems={checkedSubItems}
          clearSelection={handleClearSelection}
          fetcher={fetcher}
          getAvailableTags={getAvailableTags}
          getRowCheckboxState={(rowId) => {
            return getRowCheckboxState({
              loadedFulfillmentGroupsById,
              groupId: rowId,
              checkedSubItems,
            });
          }}
          groupsWaitingForSplit={groupsWaitingForSplit}
          handleCheckClick={handleCheckClick}
          handleRowClick={handleRowClick}
          handleUpdates={handleUpdates}
          hideSlideout={hideSlideout}
          mergeDisabledReason={mergeDisabledReason}
          onDeleteView={onDeleteView}
          pageNumber={pageNumber}
          pageSize={pageSize}
          refreshSymbol={refreshSymbol}
          reservedViewNames={reservedViewNames}
          rpcClient={rpcClient}
          saveView={saveView}
          saveViewLoad={saveViewLoad}
          searchRefreshSymbol={searchRefreshSymbol}
          selectedRows={selectedGroupIds}
          setGroupsWaitingForReplacement={setGroupsWaitingForReplacement}
          setHideSlideout={setHideSlideout}
          setMergeModalOpen={handleMergeModalOpen}
          setPageNumber={setPageNumber}
          setPageSize={setPageSize}
          setViewBeingEdited={handleViewEditingChange}
          showMergeButton={showMergeButton}
          slideoutOpen={slideoutOpen}
          someChecked={rows.length > 0 && checkedSubItems.size > 0}
          tableRef={tableRef}
          toggleAllSelection={toggleAllSelection}
          toggleSubItemSelection={toggleSubItemSelection}
          updateRowData={handleUpdates}
          users={teamUsers}
          viewBeingEdited={viewBeingEdited}
        />
      </div>
      <OrderFulfillmentSlideout
        availableUsers={teamUsers}
        balance={balanceLoad?.value?.balance}
        balanceLoading={balanceLoad.pending}
        deselectComplete={() => {
          const newRows = new Set(
            Array.from(selectedGroupIds).filter((id) => {
              const row = rows.find((row) => row._id === id);
              return row && canPurchaseLabelStatus(row.status);
            }),
          );
          setCheckedSubItems(
            getCheckedSubItemsForRows({
              loadedFulfillmentGroupsById,
              newSelectionIds: newRows,
              checkedSubItems,
            }),
          );
          setSelectedGroupIds(newRows);
        }}
        deselectGroups={deselectGroups}
        doPartialSplit={doPartialSplit}
        fulfillmentGroups={selectedFulfillmentGroups}
        hideSlideout={hideSlideout}
        onChangeLocations={setGroupsWaitingForReplacement}
        onClose={() => {
          setSelectedGroupIds(new Set());
          setCheckedSubItems(new Map());
        }}
        onExportPickListCsv={async () => {
          try {
            const response = await rpcClient.getPickListCsv({
              fulfillmentGroupIds: selectedFulfillmentGroups.map((g) => g._id),
            });
            const link = document.createElement("a");
            link.href = response.url;
            link.download = "pick_list.csv";
            document.body.append(link);
            link.click();
            link.remove();
          } catch (error) {
            console.error("Error exporting pick list CSV", error);
            toast("Error exporting pick list CSV", { variant: "error" });
          }
        }}
        onGroupsFulfillment={onGroupsFulfillment}
        onPrintOrderSummary={() => {
          batchPrinting.printBatch({
            batch: {
              fulfillmentGroupIds: selectedFulfillmentGroups.map((g) => g._id),
            },
            document: PrintableDocument.ORDER_SUMMARY,
            documentPrintStatusMap:
              getDocumentPrintStatusMapForFulfillmentGroups(
                selectedFulfillmentGroups,
              ),
          });
        }}
        onPrintPackingSlip={() => {
          batchPrinting.printBatch({
            batch: {
              fulfillmentGroupIds: selectedFulfillmentGroups.map((g) => g._id),
            },
            document: PrintableDocument.PACKING_SLIP,
            documentPrintStatusMap:
              getDocumentPrintStatusMapForFulfillmentGroups(
                selectedFulfillmentGroups,
              ),
            somePrinted: selectedFulfillmentGroups.some(
              (g) => g.printStatus?.[PrintKind.PACKING_SLIP],
            ),
            packingSlipIds: selectedFulfillmentGroups
              .map((g) => {
                if (g.packingSlipTemplateId) {
                  return g.packingSlipTemplateId;
                }
                return team?.settings?.outboundLabels?.packingSlipId;
              })
              .filter((id): id is string => id !== undefined),
          });
        }}
        onPrintPickList={() => {
          batchPrinting.printBatch({
            batch: {
              fulfillmentGroupIds: selectedFulfillmentGroups.map((g) => g._id),
            },
            document: PrintableDocument.PICK_LIST,
            documentPrintStatusMap:
              getDocumentPrintStatusMapForFulfillmentGroups(
                selectedFulfillmentGroups,
              ),
          });
        }}
        onVoidGroups={setGroupsWaitingForReplacement}
        parcelValues={parcelValues}
        partialSplitLoad={partialSplitLoad}
        reloadBalance={() => reloadBalance()}
        resetSelections={() => {
          const newRows = new Set<string>();
          setSelectedGroupIds(newRows);
          setCheckedSubItems(new Map());
        }}
        selectFullGroups={() => {
          const newCheckedSubItems = new Map(checkedSubItems);

          for (const groupId of selectedGroupIds) {
            const checkboxState = getRowCheckboxState({
              loadedFulfillmentGroupsById,
              groupId,
              checkedSubItems,
            });

            if (checkboxState === "indeterminate") {
              const group = loadedFulfillmentGroups.find(
                (g) => g._id === groupId,
              );
              if (group) {
                newCheckedSubItems.set(groupId, getRowSelectedSet(group));
              }
            }
          }

          setCheckedSubItems(newCheckedSubItems);
        }}
        setRows={setRows}
        setSearchRefreshSymbol={setSearchRefreshSymbol}
        setSelectedFulfillmentGroups={setSelectedFulfillmentGroups}
        somePartiallySelected={somePartiallySelected}
        triggerRefresh={refreshTable}
        updateRows={handleUpdates}
        updatesPending={updatesPending}
      />
      {actionsPortal &&
        createPortal(
          <Flex>
            <RedoButton
              aria-label="Order actions"
              hierarchy="secondary"
              IconLeading={DotsHorizontalIcon}
              onClick={() => setActionsDropdownOpen((open) => !open)}
              ref={setActionsDropdownAnchor}
              size="sm"
              theme="normal"
            />
            <RedoListDropdown<OmsHeaderAction>
              dropdownAnchor={actionsDropdownAnchor}
              dropdownOpen={actionsDropdownOpen}
              fitToAnchor={false}
              items={[
                ...(mergeSuggestions.length > 0
                  ? [
                      {
                        id: OmsHeaderAction.ViewMergeSuggestions,
                        value: OmsHeaderAction.ViewMergeSuggestions,
                        type: "text" as const,
                        text: `View ${mergeSuggestions.length} merge suggestions`,
                        leadingItem: {
                          type: "icon" as const,
                          Icon: PackagePlusIcon,
                        },
                      },
                    ]
                  : []),
                {
                  id: OmsHeaderAction.ImportOrders,
                  value: OmsHeaderAction.ImportOrders,
                  type: "text" as const,
                  text: "Import orders",
                },
                {
                  id: OmsHeaderAction.CreateNewOrder,
                  value: OmsHeaderAction.CreateNewOrder,
                  type: "text" as const,
                  text: "Create new order",
                  leadingItem: { type: "icon" as const, Icon: Plus },
                },
              ]}
              itemSelected={({ item }) => {
                switch (item.value) {
                  case OmsHeaderAction.ViewMergeSuggestions:
                    amplitude.logEvent(
                      "oms-fulfillment-merge-suggestions-clicked",
                      { suggestionCount: mergeSuggestions.length },
                    );
                    hideNotification();
                    setMergeSuggestionCleared(new Date());
                    setSuggestionModalOpen(true);
                    break;
                  case OmsHeaderAction.ImportOrders:
                    openImportOrdersModal();
                    break;
                  case OmsHeaderAction.CreateNewOrder:
                    amplitude.logEvent("oms-fulfillment-create-order-clicked", {
                      viewId: activeView?._id,
                      viewType: activeView
                        ? new Set<string>(
                            Object.values(OutboundSystemView),
                          ).has(activeView.name)
                          ? "system"
                          : "custom"
                        : "unknown",
                    });
                    setCreateOrderSlideoutOpen(true);
                    break;
                }
                setActionsDropdownOpen(false);
              }}
              selectedItems={undefined}
              setDropdownOpen={setActionsDropdownOpen}
            />
          </Flex>,
          actionsPortal,
        )}
      <CreateOrderSlideout
        addGroupsToTable={addManualGroupsToTable}
        onClose={() => setCreateOrderSlideoutOpen(false)}
        open={createOrderSlideoutOpen}
      />
      {importOrdersModalOpen && (
        <ImportOrdersModal
          open={importOrdersModalOpen}
          setOpen={setImportOrdersModalOpen}
          triggerReload={waitForRefresh}
        />
      )}
    </PinVerificationProvider>
  );
});
